"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { useEffect, useId, useState, type FormEvent } from "react";
import {
  isCertificatePublicNumberFormat,
  normalizeCertificatePublicNumber,
  verifyCertificate,
} from "@/lib/api/certificates";
import { isApiError } from "@/lib/api/client";
import { useAuth } from "@/lib/context/AuthProvider";
import { useLocale } from "@/lib/context/LocaleProvider";
import { useDocumentTitle } from "@/lib/hooks/useDocumentTitle";
import { FieldError, FieldLabel, TextInput } from "@/components/ui/fields";
import { FormAlert } from "@/components/ui/FormAlert";
import { CircleCheck, CircleClose, ShieldCheck } from "@/components/ui/icons";

function formatMessage(
  template: string,
  vars: Record<string, string | number>,
): string {
  return template.replace(/\{(\w+)\}/g, (_, name: string) =>
    String(vars[name] ?? ""),
  );
}

function formatIssuedAt(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date);
  } catch (err) {
    console.error("Failed to format certificate issuedAt", err);
    return iso;
  }
}

type ResultState =
  | { kind: "idle" }
  | { kind: "valid"; issuedAt: string; publicNumber: string }
  | { kind: "invalid"; publicNumber: string };

type Props = {
  /** Prefill from `/certificates/verify/[publicNumber]`. */
  initialPublicNumber?: string;
};

/** Public /certificates/verify — authenticity check by serial number (no auth). */
export function CertificateVerifyScreen({ initialPublicNumber }: Props) {
  const { t, locale } = useLocale();
  const { isAuthenticated } = useAuth();
  const searchParams = useSearchParams();
  useDocumentTitle(t.certsVerifyTitle);

  const inputId = useId();
  const errorId = useId();

  const queryNumber =
    searchParams.get("n")?.trim() ||
    searchParams.get("number")?.trim() ||
    "";

  const seed = normalizeCertificatePublicNumber(
    initialPublicNumber?.trim() || queryNumber || "",
  );

  const [value, setValue] = useState(seed);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [checking, setChecking] = useState(() =>
    Boolean(seed) && isCertificatePublicNumberFormat(seed),
  );
  const [result, setResult] = useState<ResultState>({ kind: "idle" });

  async function runVerify(raw: string) {
    const publicNumber = normalizeCertificatePublicNumber(raw);
    setValue(publicNumber);
    setFormError(null);
    setFieldError(null);
    setResult({ kind: "idle" });

    if (!publicNumber) {
      setFieldError(t.certsVerifyRequired);
      return;
    }
    if (!isCertificatePublicNumberFormat(publicNumber)) {
      setFieldError(t.certsVerifyFormatError);
      return;
    }

    setChecking(true);
    try {
      const data = await verifyCertificate(publicNumber);
      if (data.valid) {
        setResult({
          kind: "valid",
          issuedAt: data.issuedAt ?? "",
          publicNumber,
        });
      } else {
        setResult({ kind: "invalid", publicNumber });
      }
    } catch (err) {
      console.error("Certificate verification failed", err);
      const message =
        isApiError(err) && err.message
          ? err.message
          : t.certsVerifyError;
      setFormError(message);
    } finally {
      setChecking(false);
    }
  }

  // Deep link / query: prime the form for a new public number during render, so
  // the effect below only owns the request itself.
  const seedIsValid = Boolean(seed) && isCertificatePublicNumberFormat(seed);
  const [seededFrom, setSeededFrom] = useState<string | null>(null);

  if (seed && seededFrom !== seed) {
    setSeededFrom(seed);
    setValue(seed);
    setFormError(null);
    setFieldError(seedIsValid ? null : t.certsVerifyFormatError);
    setResult({ kind: "idle" });
    setChecking(seedIsValid);
  }

  useEffect(() => {
    if (!seedIsValid) return;

    let cancelled = false;

    (async () => {
      try {
        const data = await verifyCertificate(seed);
        if (cancelled) return;
        if (data.valid) {
          setResult({
            kind: "valid",
            issuedAt: data.issuedAt ?? "",
            publicNumber: seed,
          });
        } else {
          setResult({ kind: "invalid", publicNumber: seed });
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Certificate verification failed", err);
        setFormError(isApiError(err) && err.message ? err.message : t.certsVerifyError);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [seed, seedIsValid, t.certsVerifyError]);

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void runVerify(value);
  }

  function onReset() {
    setResult({ kind: "idle" });
    setFormError(null);
    setFieldError(null);
    setValue("");
  }

  return (
    <section className="animate-fade-up px-5 pb-10 pt-7">
      <div className="mx-auto max-w-[520px]">
        <p className="mb-3 text-[10.5px] font-bold uppercase tracking-[0.7px] text-muted-2">
          {t.certsVerifyEyebrow}
        </p>
        <h1 className="text-[32px] font-medium leading-[1.05] tracking-[-1.28px] text-ink">
          {t.certsVerifyTitle}
        </h1>
        <p className="mt-2.5 max-w-[480px] text-[15px] leading-[1.45] text-ink-3 [text-wrap:pretty]">
          {t.certsVerifyDesc}
        </p>

        <div className="card-shadow relative mt-7 overflow-hidden rounded-[22px] border border-[rgba(26,24,22,0.05)] bg-white">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-gold/80 via-accent/55 to-gold/40"
          />
          <div className="px-5 py-7 sm:px-7 sm:py-8">
            <div className="mb-6 flex items-start gap-3.5">
              <div className="flex h-12 w-12 flex-none items-center justify-center rounded-[16px] bg-accent-soft text-accent">
                <ShieldCheck size={24} />
              </div>
              <div className="min-w-0 pt-0.5">
                <p className="text-[15px] font-semibold tracking-[-0.2px] text-ink">
                  {t.certsVerifyWipa}
                </p>
                <p className="mt-1 text-[13px] leading-[1.45] text-muted [text-wrap:pretty]">
                  {t.certsVerifyPrivacy}
                </p>
              </div>
            </div>

            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <div>
                <FieldLabel htmlFor={inputId}>{t.certsVerifyLabel}</FieldLabel>
                <TextInput
                  id={inputId}
                  name="publicNumber"
                  value={value}
                  onChange={(event) => {
                    setValue(event.target.value.toUpperCase());
                    setFieldError(null);
                    if (result.kind !== "idle") setResult({ kind: "idle" });
                  }}
                  placeholder={t.certsVerifyPlaceholder}
                  autoComplete="off"
                  spellCheck={false}
                  inputMode="text"
                  invalid={Boolean(fieldError)}
                  aria-describedby={
                    fieldError
                      ? errorId
                      : undefined
                  }
                  className="font-medium tracking-[0.04em]"
                />
                {fieldError ? (
                  <FieldError id={errorId}>{fieldError}</FieldError>
                ) : (
                  <p className="mt-2 text-[12.5px] leading-[1.4] text-muted-2">
                    {t.certsVerifyHint}
                  </p>
                )}
              </div>

              {formError ? <FormAlert>{formError}</FormAlert> : null}

              <button
                type="submit"
                disabled={checking}
                className="flex h-[48px] w-full items-center justify-center rounded-full bg-accent px-6 text-[14px] font-semibold text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-55"
              >
                {checking ? t.certsVerifyChecking : t.certsVerifyCta}
              </button>
            </form>

            {result.kind === "valid" ? (
              <div
                role="status"
                className="mt-6 animate-fade-up rounded-[18px] border border-accent/15 bg-accent-soft/70 px-4 py-4 sm:px-5"
              >
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-full bg-white text-accent shadow-[0_1px_0_rgba(45,32,50,0.06)]">
                    <CircleCheck size={20} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[15.5px] font-semibold tracking-[-0.2px] text-ink">
                      {t.certsVerifyValidTitle}
                    </p>
                    {result.issuedAt ? (
                      <p className="mt-1.5 text-[13.5px] leading-[1.5] text-ink-3 [text-wrap:pretty]">
                        {formatMessage(t.certsVerifyValidBody, {
                          date: formatIssuedAt(result.issuedAt, locale),
                        })}
                      </p>
                    ) : null}
                    <p className="mt-3 text-[12.5px] font-semibold tracking-[0.06em] text-ink-4">
                      {result.publicNumber}
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            {result.kind === "invalid" ? (
              <div
                role="status"
                className="mt-6 animate-fade-up rounded-[18px] border border-[rgba(26,24,22,0.08)] bg-surface px-4 py-4 sm:px-5"
              >
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-full bg-white text-ink-4 shadow-[0_1px_0_rgba(45,32,50,0.06)]">
                    <CircleClose size={20} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[15.5px] font-semibold tracking-[-0.2px] text-ink">
                      {t.certsVerifyInvalidTitle}
                    </p>
                    <p className="mt-1.5 text-[13.5px] leading-[1.5] text-ink-3 [text-wrap:pretty]">
                      {t.certsVerifyInvalidBody}
                    </p>
                    <p className="mt-3 text-[12.5px] font-semibold tracking-[0.06em] text-muted">
                      {result.publicNumber}
                    </p>
                    <button
                      type="button"
                      onClick={onReset}
                      className="mt-3.5 text-[13.5px] font-semibold text-accent transition hover:opacity-80"
                    >
                      {t.certsVerifyAgain}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {isAuthenticated ? (
          <p className="mt-5 text-center text-[13px] leading-[1.45] text-muted">
            <Link
              href="/certificates"
              className="font-semibold text-accent transition hover:opacity-80"
            >
              {t.certsVerifyBackToCerts}
            </Link>
          </p>
        ) : null}
      </div>
    </section>
  );
}
